// Rules for VALIDATION_CONSTRAINTS.md section 10 — CallActivity / SubProcess integrity.
// Some rules here are cross-process and use scope: 'diagram' to see every process in ctx.
import { registerRule } from '../registry';
import { ValidationRule, ValidationIssue } from '../types';
import { labelOf } from '../helpers';

// 10.1: CallActivity.processName must be non-empty.
const rule_10_1_ProcessNameRequired: ValidationRule = {
  id: '10.1',
  description: 'CallActivity requires a non-empty processName',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'CallActivity') continue;
      const name = (n.data?.processName ?? '').toString().trim();
      if (!name) {
        issues.push({
          severity: 'error',
          rule: '10.1',
          message: `${labelOf(n)} requires a non-empty processName.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_10_1_ProcessNameRequired);

// 10.2: CallActivity.processName must resolve to a process in the diagram.
const rule_10_2_ProcessNameResolvable: ValidationRule = {
  id: '10.2',
  description: 'CallActivity.processName must match a process in the diagram',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    const processNames = new Set(ctx.processes.map(p => p.name));
    for (const proc of ctx.processes) {
      for (const n of proc.nodes) {
        if (n.type !== 'CallActivity') continue;
        const name = (n.data?.processName ?? '').toString().trim();
        if (!name) continue; // reported by 10.1
        if (!processNames.has(name)) {
          issues.push({
            severity: 'error',
            rule: '10.2',
            message: `${labelOf(n)} references unknown process "${name}" (not found in diagram).`,
            nodeIds: [n.id],
            processId: proc.id,
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_10_2_ProcessNameResolvable);

// 10.3: Called process should be marked isSubProcess = true.
const rule_10_3_CalledIsSubProcess: ValidationRule = {
  id: '10.3',
  description: 'CallActivity should call processes marked as sub-processes',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    const byName = new Map(ctx.processes.map(p => [p.name, p]));
    for (const proc of ctx.processes) {
      for (const n of proc.nodes) {
        if (n.type !== 'CallActivity') continue;
        const name = (n.data?.processName ?? '').toString().trim();
        if (!name) continue;
        const called = byName.get(name);
        if (!called) continue; // reported by 10.2
        if (!called.isSubProcess) {
          issues.push({
            severity: 'warning',
            rule: '10.3',
            message: `${labelOf(n)} calls process "${name}" which is not marked as a sub-process.`,
            nodeIds: [n.id],
            processId: proc.id,
          });
        }
      }
    }
    return issues;
  },
};
registerRule(rule_10_3_CalledIsSubProcess);

// 10.4: Skipped — async SubProcess toggle is hardcoded off at the UI/type/export layer
//       (DiagramCallActivityNode.ToNode forces AsAsyncCall=false). No editor rule needed.

// 10.5: CallActivity.processVersion must be non-negative when present.
//       Engine rewrites 0/undefined to parent diagram version at load.
const rule_10_5_ProcessVersionNonNegative: ValidationRule = {
  id: '10.5',
  description: 'CallActivity.processVersion must be non-negative',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'CallActivity') continue;
      const version = n.data?.processVersion;
      if (typeof version === 'number' && version < 0) {
        issues.push({
          severity: 'error',
          rule: '10.5',
          message: `${labelOf(n)} has negative processVersion (${version}); must be non-negative.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_10_5_ProcessVersionNonNegative);

// 10.6: Cyclic call graph across processes is rejected.
//       Build directed graph processName -> called processName; DFS for cycles.
const rule_10_6_NoCyclicCallGraph: ValidationRule = {
  id: '10.6',
  description: 'Call graph across processes must not contain cycles',
  scope: 'diagram',
  run: (ctx) => {
    const issues: ValidationIssue[] = [];
    const processNames = new Set(ctx.processes.map(p => p.name));
    // Build adjacency: process.name -> Set<calledProcessName>
    const graph = new Map<string, Set<string>>();
    for (const proc of ctx.processes) {
      const targets = new Set<string>();
      for (const n of proc.nodes) {
        if (n.type !== 'CallActivity') continue;
        const name = (n.data?.processName ?? '').toString().trim();
        if (name && processNames.has(name)) targets.add(name);
      }
      graph.set(proc.name, targets);
    }

    // Iterative DFS with path tracking, per-node color (0=unvisited, 1=on stack, 2=done).
    const color = new Map<string, number>();
    const reported = new Set<string>();

    const dfs = (start: string) => {
      const stack: Array<{ node: string; iter: Iterator<string> }> = [];
      const path: string[] = [];
      const onPath = new Set<string>();

      const push = (node: string) => {
        color.set(node, 1);
        path.push(node);
        onPath.add(node);
        stack.push({ node, iter: (graph.get(node) ?? new Set<string>()).values() });
      };

      push(start);
      while (stack.length > 0) {
        const top = stack[stack.length - 1];
        const next = top.iter.next();
        if (next.done) {
          color.set(top.node, 2);
          onPath.delete(top.node);
          path.pop();
          stack.pop();
          continue;
        }
        const child: string = next.value;
        const c = color.get(child) ?? 0;
        if (c === 1) {
          // Cycle detected from child back to child in path.
          const startIdx = path.indexOf(child);
          const cyclePath = [...path.slice(startIdx), child];
          const key = [...cyclePath].sort().join('|');
          if (!reported.has(key)) {
            reported.add(key);
            issues.push({
              severity: 'error',
              rule: '10.6',
              message: `Cyclic call graph detected: ${cyclePath.join(' -> ')}`,
            });
          }
        } else if (c === 0) {
          push(child);
        }
      }
    };

    for (const name of graph.keys()) {
      if ((color.get(name) ?? 0) === 0) dfs(name);
    }
    return issues;
  },
};
registerRule(rule_10_6_NoCyclicCallGraph);

// 10.7: A process cannot call itself directly.
const rule_10_7_NoSelfCall: ValidationRule = {
  id: '10.7',
  description: 'A process cannot contain a CallActivity that calls itself',
  scope: 'process',
  run: (_ctx, process) => {
    if (!process) return [];
    const issues: ValidationIssue[] = [];
    for (const n of process.nodes) {
      if (n.type !== 'CallActivity') continue;
      const name = (n.data?.processName ?? '').toString().trim();
      if (name && name === process.name) {
        issues.push({
          severity: 'error',
          rule: '10.7',
          message: `${labelOf(n)} calls its own process "${process.name}" — direct self-call is not allowed.`,
          nodeIds: [n.id],
        });
      }
    }
    return issues;
  },
};
registerRule(rule_10_7_NoSelfCall);
